import Turbolinks from 'turbolinks'

/*doc
---
title: Google Analytics Tracking
name: track
category: Javascript - General
---

## Event Tracking Tutorials

- [GTM Enhanced Ecommerce (UA) Developer Guide](https://developers.google.com/tag-manager/enhanced-ecommerce#details)
- [GTM Ecommerce Tips](https://www.simoahava.com/?s=ecommerce)
- [Use eventTimeout With eventCallback](https://www.simoahava.com/gtm-tips/use-eventtimeout-eventcallback/)
- [TODO: Custom metrics](https://developers.google.com/tag-manager/enhanced-ecommerce#custom-metrics)

## Data Types

### Impression Data

Represents information about a product that has been viewed. It is referred
to as an `impressionFieldObject` and contains the following values:

| Param         | Type      | Description                                                                                                                                         |
|---------------|-----------|-----------------------------------------------------------------------------------------------------------------------------------------------------|
| id            | string    | The product ID or SKU (e.g. P67890). *Either this field or name must be set*.                                                                       |
| name          | string    | The name of the product (e.g. Android T-Shirt). *Either this field or id must be set*.                                                              |
| list          | string    | The list or collection to which the product belongs (e.g. Search Results)                                                                           |
| brand         | string    | The brand associated with the product (e.g. Google).                                                                                                |
| category      | string    | The category to which the product belongs (e.g. Apparel). Use / as a delimiter to specify up to 5-levels of hierarchy (e.g. Apparel/Men/T-Shirts).  |
| variant       | string    | The variant of the product (e.g. Black).                                                                                                            |
| position      | integer   | The product's position in a list or collection (e.g. 2).                                                                                            |
| price         | float     | The price of a product (e.g. 29.20).                                                                                                                |

### Product Data

Product data represents individual products that were viewed, added to the shopping cart, etc.
It is referred to as a `productFieldObject` and contains the following values:

| Param         | Type      | Description                                                                                                                                         |
|---------------|-----------|-----------------------------------------------------------------------------------------------------------------------------------------------------|
| id            | string    | The product ID or SKU (e.g. P67890). *Either this field or name must be set*.                                                                       |
| name          | string    | The name of the product (e.g. Android T-Shirt). *Either this field or id must be set*.                                                              |
| brand         | string    | The brand associated with the product (e.g. Google).                                                                                                |
| category      | string    | The category to which the product belongs (e.g. Apparel). Use / as a delimiter to specify up to 5-levels of hierarchy (e.g. Apparel/Men/T-Shirts).  |
| variant       | string    | The variant of the product (e.g. Black).                                                                                                            |
| price         | float     | The price of a product (e.g. 29.20).                                                                                                                |
| quantity      | integer   | The quantity of a product (e.g. 2).                                                                                                                 |
| coupon        | string    | The coupon code associated with a product (e.g. SUMMER_SALE13).                                                                                     |
| position      | integer   | The product's position in a list or collection (e.g. 2).                                                                                            |

### Promo Data

Represents information about a promotion that has been viewed. It is referred
to as a `promoFieldObject` and contains the following values:

| Param         | Type      | Description                                                                         |
|---------------|-----------|-------------------------------------------------------------------------------------|
| id            | string    | The promotion ID (e.g. PROMO_1234). *Either this field or name must be set.         |
| name          | string    | The name of the promotion (e.g. Summer Sale). *Either this field or id must be set. |
| creative      | string    | The creative associated with the promotion (e.g. summer_banner2).                   |
| position      | string    | The position of the creative (e.g. banner_slot_1).                                  |

### Action Data

Represents information about an ecommerce related action that has taken place.
It is referred to as an actionFieldObject.

#### Product and Promotion Actions
Actions specify how to interpret product and promotion data that you send to Google Analytics.

- *click* A click on a product or product link for one or more products.
- *detail* A view of product details.
- *add* Adding one or more products to a shopping cart.
- *remove* Remove one or more products from a shopping cart.
- *checkout* Initiating the checkout process for one or more products.
- *checkout_option* Sending the option value for a given checkout step.
- *purchase* The sale of one or more products.
- *refund* The refund of one or more products.
- *promo_click* A click on an internal promotion.

| Param         | Type      | Description                                                                                                                                                                                                                                                                                                                                                                                     |
|---------------|-----------|-------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------|
| action        | string    | see above for different types of actions.                                                                                                                                                                                                                                                                                                                                                       |
| id            | string    | The transaction ID (e.g. T1234). *Required if the action type is purchase or refund.                                                                                                                                                                                                                                                                                                            |
| affiliation   | string    | The store or affiliation from which this transaction occurred (e.g. Google Store).                                                                                                                                                                                                                                                                                                              |
| revenue       | float     | Specifies the total revenue or grand total associated with the transaction (e.g. 11.99). This value may include shipping, tax costs, or other adjustments to total revenue that you want to include as part of your revenue calculations. Note: if revenue is not set, its value will be automatically calculated using the product quantity and price fields of all products in the same hit.  |
| tax           | float     | The total tax associated with the transaction.                                                                                                                                                                                                                                                                                                                                                  |
| shipping      | float     | The shipping cost associated with the transaction.                                                                                                                                                                                                                                                                                                                                              |
| coupon        | string    | The transaction coupon redeemed with the transaction.                                                                                                                                                                                                                                                                                                                                           |
| list          | string    | The list that the associated products belong to. Optional.                                                                                                                                                                                                                                                                                                                                      |
| step          | integer   | A number representing a step in the checkout process. Optional on checkout actions.                                                                                                                                                                                                                                                                                                             |
| option        | string    | Additional field for checkout and checkout_option actions that can describe option information on the checkout page, like selected payment method.                                                                                                                                                                                                                                              |


## Actions

### Track Product Impessions

Product impressions are used to specify where on the site a product was viewed,
but not for recording the product details page. See `product detail` for recording
that. Also triggers a `productImpression` event.

Be sure to include a unique list name and the position the product was viewed at.
So for example on the "Item Added Recommendations" the list name might be "Item Added Recommendations",
and every product would include the list name and position number (starting with 1).

#### JS Example

Pass a single `ProductFieldObject` or array of multiple `ProductFieldObject`s
to mark the product(s) as being viewed. Be sure to include a list name and
position for each product.

```js_example
var products = [...]  // `ProductFieldObject`s

$.track('product impression', products)
```

#### HTML Example

Using `data` attributes, a list of products may have impressions recorded as
well as setup the product click recording automatically.

Add `data-product-sku`, `data-product-name`, `data-product-brand`,
`data-product-category`, `data-position`, and `data-price` to each product.

*Tracking Product Clicks*
Add a `data-track-product-click` attribute to every link that should be tracked.
By adding this to a link inside the product, it will record the product is being
clicked on, so the link should pretty much be to the product/variant page.
You can also include `data-sku` and `data-price` to the link to specify a link
going to a different variant of the product.

```html_example
<div class="products">
  <div
    class="product"
    data-product-sku="123456"
    data-product-name="Book Name"
    data-product-brand="Author Name"
    data-product-category="Books/eBooks/Plus"
    data-position=0
    data-price="12.95"
  >
    // This link tracks using the default product values.
    <a href="/p/product-link" data-track-product-click>
      Book Name
    </a>

    // This link tracks a separate variant as being clicked.
    <a
      href="/p/product-link"
      data-track-product-click
      data-variant-sku="23454"
      data-variant-price="15.99"
    >
      Other Variant
    </a>
  </div>
  ...
</div>

<script>
  $('.products')
    .track({
    type: 'product impression',
    selector: '.product',
    list: 'product recommendations',
    timeout: 500
  })
</script>
```

### Product Click

Tracks a product as being clicked on. This one accepts a callback that will
be performed once the action has been recorded. Usually this callback would
change the route to the product that was clicked. This will also trigger a
`productClicked` event.

```js_example
var products = {  }  // Product that was clicked
var callback = function() { location.href= '/cart' }
var timeout = 500

$.track('product click', products, listName, callback, timeout)
```

### Product details

Tracks a product's details as having been viewed. This is different from a
`product impression` as a `product impression` is more for when the product is
being displayed in a list or group of other products. Also triggers a `productDetails`
event of the event is non-interactive and `variantDetailsClick` if the user triggered the event.

So pass in `true` if the user causes the details to be viewed (like clicking in the variant picker),
and `false` if the details are viewed on page load.

```js_example
var products = {  }  // Product/variant that was clicked
var isInteractive = true

$.track('product details', product, isInteractive)
```

### Add to Cart

Tracks an item(s) as being added to the cart. Be sure to include price and
quantity as well. Also triggers an `addToCart` event.

```js_example
var products = {  }  // Product/variant(s) that was(were) added to the cart.
var callback = function() { location.href = '/cart' }
var timeout = 500

$.track('add to cart', products, callback, timeout)
```

### Remove From Cart

Tracks an item(s) as being added to the cart. Be sure to include price and
quantity as well.

```js_example
var products = {  }  // Product/variant(s) that was(were) removed from cart.
var callback = function() { location.href = '/cart' }
var timeout = 500

$.track('remove from cart', products, callback, timeout)
```

### Promo Impression

Tracks a promo impression. A promo would be used to track most of the stuff we
load from Prismic that is added by the marketing team. So the "mini banners", or
the "rotators" would be considered promos. Triggers a `promotionImpression` event.

```js_example
var promos = [{ }] // promo(s) that are being viewed.

$.track('promo impression', promos)
```

### Promo Click

Records a promotion as having been clicked. Executes the callback once that is
finished. The callback should usually be a function that changes the page or displays
more information to the user. Also triggers a `promoClick` event.

```js_example
var promos = {  }  // Promo(s) that was(were) clicked.
var callback = function() { location.href = '/platinum' }
var timeout = 500

$.track('promo click', promos, callback, timeout)
```

### Checkout Step

Records a checkout step as having occured with the given product(s). Also
triggers the given event.

```js_example
var event = 'address'
var step = 2
var prducts = []  // Product(s) in cart
var callback = function() { location.href = '/next-step' }
var timeout = 500

$.track('checkout step', event, step, products, callback, timeout)
```

### Purchase
Records the given product(s) as having been purchased by the user.

```js_example
var prducts = []  // Product(s) that were purchased

$.track('purchase', products)
```

### Refund
TODO: Not Implemented

*/
(function($) {
  'use strict'

  $.fn.track = track
  $.extend({
    track: track
  })

  $.fn.track.defaults = {
    type: 'product impression',
    selector: '.product',
    timeout: 500,
  }

  function track(opts) {
    var options,
    $elem,
    actions = {
      'product impression': trackProductImpression,
      'product click': trackProductClick,
      'product details': trackProductDetailsImpression,
      'add to cart': trackAddToCart,
      'remove from cart': trackRemoveFromCart,
      'promo impression': trackPromotionImpression,
      'promo click': trackPromotionClick,
      'checkout step': trackCheckoutStep,
      'purchase': trackPurchase,
      'event': trackEvent,
    }

    // Handle one time tracking.
    if (typeof opts === 'string') {
      if (opts in actions) {
        var args = [].slice.call(arguments) // convert args to array
        options = $.extend({}, $.fn.track.defaults)
        args.shift() // remove the action.
        actions[opts].apply(this, args)
      } else {
        throw new Error('"' + opts + '" action not available for DB tracking plugin.')
      }

    // Handle DOM related tracking.
    } else {
      options = $.extend({}, $.fn.track.defaults, opts)
      $elem = $(this)

      initialize()
    }

    return this

    /**
    * Initializes data and figures out which type of tracking should be performed.
    */
    function initialize() {
      window.dataLayer = window.dataLayer || []

      if (options.type === 'product impression') {
        compileProductImpressions()
      } else if (options.type === 'promo impression') {
        compilePromotionImpressions()
      } else {
        throw new Error('DB Tracking: Missing, or unknown tracking type given.')
      }
    }

    /**
    * Records an event and then runs either the callback or sets the page's
    * location to the elements href attribute.
    * If the user has GTM blocked then the callback is immediately called.
    *
    * @param category {string} event category
    * @param action {string} event action
    * @param label {string} event label
    * @param value {number} event value
    * @param isInteractive {boolean} if event is interactive or not (default true)
    * @param callback {function} function to run on finish
    * @param category {number} time to wait for function to finish
    */
    function trackEvent(category, action, label, value, isInteractive, callback, timeout) {
      var trackable
      if (!category || !action) {
        throw new Error('DB Tracking: category and action are required for event tracking.')
      }
      if (typeof isInteractive !== 'boolean') {
        isInteractive = true
      }

      // Perform callback and return if event tracking is disabled by user.
      if (trackingBlocked()) {
        if (callback) {
          callback()
        }
        return
      }

      // Build event data.
      trackable = {
        'event' : isInteractive ? 'interactiveEvent' : 'noninteractiveEvent',
        'eventCategory': category,
        'eventAction': action,
        'eventLabel': label,
        'eventValue': value,
      }

      // Add callback if callback or link present.
      if (callback) {
        trackable['eventCallback'] = callback
        trackable['eventTimeout'] = timeout || options.timeout
      }

      // Record event.
      debug.analytics('EVENT', trackable)
      window.dataLayer.push(trackable)
    }

    /**
    * Records the given product(s) as having been viewed.
    *
    * https://developers.google.com/tag-manager/enhanced-ecommerce#product-impressions
    * HOMEPAGE TODO: Allow these to be "non-interactive" and "interactive"
    *
    * @param {object|object[]} impressionData - Accepts either a ImpressionFieldObject or array of ImpressionFieldObject. See ImpressionFieldObject class for format.
    */
    function trackProductImpression(impressionData) {
      var impressions, trackable

      // Clean impression data
      impressions = formatData(impressionData, ImpressionFieldObject)
      if (!impressions.length) {
        return
      }
      trackable = {
        'event': 'productImpression',
        'ecommerce': {
          'impressions': impressions
        }
      }
      debug.analytics('PRODUCT IMPRESSION', trackable)
      dataLayer.push(trackable)
    }

    /**
    * Records a product as having been clicked, if a callback is present then
    * the callback is performed after the event has been recorded.
    * If GTM is blocked, then the callback is performed and the method returns.
    *
    * https://developers.google.com/tag-manager/enhanced-ecommerce#product-clicks
    *
    * @param {object|object[]} productData - Accepts either a ProductFieldObject or array of ProductFieldObject. See ProductFieldObject class for format.
    * @param {function=} callback - Function to run after tracking is complete.
    * @param {number=} callback - Time to wait for event to record before performing callback.
    */
    function trackProductClick(productData, listName, callback, timeout) {
      var products, trackable, actionField = {}

      // If tracking is blocked, run the callback and return.
      if (trackingBlocked()) {
        if (callback) {
          callback()
        }
        return
      }
      // Clean product data
      products = formatData(productData, ProductFieldObject)
      if (!products.length) {
        return
      }
      if (listName) {
        actionField['list'] = listName
      }
      trackable = {
        'event': 'productClick',
        'ecommerce': {
          'click': {
            'actionField': actionField,
            'products': products
          }
        }
      }
      // Add callback info so GTM will execute on completion.
      if (callback) {
        trackable['eventCallback'] = callback
        trackable['eventTimeout'] = timeout || options.timeout
      }
      debug.analytics('PRODUCT CLICK', trackable)
      dataLayer.push(trackable)
    }

    /**
    * Records a product as having its product details viewed.
    *
    * https://developers.google.com/tag-manager/enhanced-ecommerce#product-details
    *
    * @param {object|object[]} productData - Accepts either a ProductFieldObject or array of ProductFieldObject. See ProductFieldObject class for format.
    * @param {boolean} isInterative - Sepcifies which event should be triggered (interactive or non-interactive)
    */
    function trackProductDetailsImpression(productData, isInterative) {
      var products, trackable

      // Clean product data
      products = formatData(productData, ProductFieldObject)
      if (!products.length) {
        return
      }
      trackable = {
        'event': isInterative ? 'variantDetailsClick' : 'productDetails',
        'ecommerce': {
          'detail': {
            'products': products
          }
        }
      }
      debug.analytics('PRODUCT DETAILS', trackable)
      dataLayer.push(trackable)
    }

    /**
    * Records a product as having been added to the cart. Also triggers the
    * `addToCart` event, and runs the callback if given.
    *
    * https://developers.google.com/tag-manager/enhanced-ecommerce#add
    *
    * @param {object|object[]} productData - Accepts either a ProductFieldObject or array of ProductFieldObject. See ProductFieldObject class for format.
    * @param {function=} callback - Function to run after tracking is complete.
    * @param {number=} callback - Time to wait for event to record before performing callback.
    */
    function trackAddToCart(productData, callback, timeout) {
      var products, trackable

      // If tracking is blocked, run the callback and return.
      if (trackingBlocked()) {
        if (callback) {
          callback()
        }
        return
      }
      // Clean product data
      products = formatData(productData, ProductFieldObject)
      if (!products.length) {
        return
      }
      trackable = {
        'event': 'addToCart',
        'ecommerce': {
          'add': {
            'products': products
          }
        }
      }
      // Add callback info so GTM will execute on completion.
      if (callback) {
        trackable['eventCallback'] = callback
        trackable['eventTimeout'] = timeout || options.timeout
      }
      debug.analytics('ADD TO CART', trackable)
      dataLayer.push(trackable)
    }

    /**
    * Records a product as having been remove from the cart. Also triggers the
    * `removeFromCart` event, and runs the callback if given.
    *
    * https://developers.google.com/tag-manager/enhanced-ecommerce#remove
    *
    * @param {object|object[]} productData - Accepts either a ProductFieldObject or array of ProductFieldObject. See ProductFieldObject class for format.
    * @param {function=} callback - Function to run after tracking is complete.
    * @param {number=} callback - Time to wait for event to record before performing callback.
    */
    function trackRemoveFromCart(productData, callback, timeout) {
      var products, trackable

      // If tracking is blocked, run the callback and return.
      if (trackingBlocked()) {
        if (callback) {
          callback()
        }
        return
      }
      // Clean product data
      products = formatData(productData, ProductFieldObject)
      if (!products.length) {
        return
      }
      trackable = {
        'event': 'removeFromCart', // TODO: do we have an existing event for this?
        'ecommerce': {
          'remove': {
            'products': products
          }
        }
      }
      // Add callback info so GTM will execute on completion.
      if (callback) {
        trackable['eventCallback'] = callback
        trackable['eventTimeout'] = timeout || options.timeout
      }
      debug.analytics('REMOVE FROM CART', trackable)
      dataLayer.push(trackable)
    }

    /**
    * Records a promotion as having been viewed by the user.
    *
    * https://developers.google.com/tag-manager/enhanced-ecommerce#promo
    *
    * @param {object|object[]} promoData - Accepts either a PromoFieldObject or array of PromoFieldObject. See PromoFieldObject class for format.
    */
    function trackPromotionImpression(promoData) {
      var promotions, trackable

      // Clean promo data
      promotions = formatData(promoData, PromoFieldObject)
      if (!promotions.length) {
        return
      }
      trackable = {
        'event': 'promotionImpression',
        'ecommerce': {
          'promoView': {
            'promotions': promotions
          }
        }
      }
      debug.analytics('PROMOTION IMPRESSION', trackable)
      dataLayer.push(trackable)
    }

    /**
    * Records a promotion as having been clicked, also triggers the 'prmotionClick'
    * event, and calls the given callback once finished. If GTM is blocked and a
    * callback is given,
    *
    * https://developers.google.com/tag-manager/enhanced-ecommerce#promo-clicks
    *
    * @param {object|object[]} promoData - Accepts either a PromoFieldObject or array of PromoFieldObject. See PromoFieldObject class for format.
    * @param {function=} callback - Function to run after tracking is complete.
    * @param {number=} callback - Time to wait for event to record before performing callback.
    */
    function trackPromotionClick(promoData, callback, timeout) {
      var promotions, trackable

      // If tracking is blocked, run the callback and return.
      if (trackingBlocked()) {
        if (callback) {
          callback()
        }
        return
      }
      // Clean promo data
      promotions = formatData(promoData, PromoFieldObject)
      if (!promotions.length) {
        return
      }
      trackable = {
        'event': 'promotionClick',
        'ecommerce': {
          'promoClick': {
            'promotions': promotions
          }
        }
      }

      // Add callback info so GTM will execute on completion.
      if (callback) {
        trackable['eventCallback'] = callback
        trackable['eventTimeout'] = timeout || options.timeout
      }
      debug.analytics('PROMOTION CLICK', trackable)
      dataLayer.push(trackable)
    }

    /**
    * Records a checkout step and records the given products as being in the cart.
    * If a callback is present then it is run on completion, or if GTM is blocked
    * then it is run immediately.
    *
    * https://developers.google.com/tag-manager/enhanced-ecommerce#promo-clicks
    *
    * @param {string} event - Name of event
    * @param {number} step - step number (1,2,3,4, etc.)
    * @param {object|object[]} productData - Accepts either a ProductFieldObject or array of ProductFieldObject. See ProductFieldObject class for format.
    * @param {function=} callback - Function to run after tracking is complete.
    * @param {number=} callback - Time to wait for event to record before performing callback.
    */
    function trackCheckoutStep(event, step, productData, callback, timeout) {
      var products, trackable

      // If tracking is blocked, run the callback and return.
      if (trackingBlocked()) {
        if (callback) {
          callback()
        }
        return
      }
      // Clean product data
      products = formatData(productData, ProductFieldObject)
      if (!products.length) {
        return
      }
      trackable = {
        'event': event,
        'ecommerce': {
          'checkout': {
            'actionField': { 'step': step },
            'products': products
          }
        }
      }
      // Add callback info so GTM will execute on completion.
      if (callback) {
        trackable['eventCallback'] = callback
        trackable['eventTimeout'] = timeout || options.timeout
      }
      debug.analytics('CHECKOUT', trackable)
      dataLayer.push(trackable)
    }

    /**
    * Records the given products as having been purchased. If a callback is given
    * then it is executed upon completion, or if GTM is blocked then it is executed
    * immediately.
    *
    * https://developers.google.com/tag-manager/enhanced-ecommerce#purchases
    *
    * @param {object|object[]} productData - Accepts either a ProductFieldObject or array of ProductFieldObject. See ProductFieldObject class for format.
    * @param {function=} callback - Function to run after tracking is complete.
    * @param {number=} callback - Time to wait for event to record before performing callback.
    */
    function trackPurchase(productData, callback, timeout) {
      var products, trackable

      // If tracking is blocked, run the callback and return.
      if (trackingBlocked()) {
        if (callback) {
          callback()
        }
        return
      }
      // Clean product data
      products = formatData(productData, ProductFieldObject)
      if (!products.length) {
        return
      }
      trackable = {
        'event': 'purchase',
        'ecommerce': {
          'purchase': {
            'actionField': { },
            'products': products
          }
        }
      }
      // Add callback info so GTM will execute on completion.
      if (callback) {
        trackable['eventCallback'] = callback
        trackable['eventTimeout'] = timeout || options.timeout
      }
      debug.analytics('PURCHASE', trackable)
      dataLayer.push(trackable)
    }

    /**
    * @function formatData
    *
    * Cleans the given data by turning it into an object of the given data type.
    *
    * @param {object|object[]} data - Data to be formatted.
    * @param {Class} dataType - Data class the data should be converted to.
    */
    function formatData(data, dataType) {
      if (Array.isArray(data)) {
        return data.map(function(datum) {
          return new dataType(datum)
        })
      } else {
        return [ new dataType(data) ]
      }
    }

    /**
    * Checks to see if the user has GTM blocked or not.
    *
    * @function formatData
    * @returns {boolean}
    */
    function trackingBlocked() {
      return !window.google_tag_manager
    }

    /**
    * Finds all child products matching the given selector and uses each product's
    * data attributes to compile a list of products that have been viewed by the
    * user. Once the product impressions are recorded, a click event is attached
    * to all links in the product that have a `data-track-product-click` attribute.
    * When one of these links is clicked, a product click event is recorded.
    *
    * @function compileProductImpressions
    */
    function compileProductImpressions() {
      var products = $elem.find(options.selector),
      productData

      // First track all product impressions.
      productData = products
        .toArray()
        .map(function(product) {
          var data = $(product).data()
          return {
            id: data.productSku,
            product_id: data.productId,
            name: data.productName,
            brand: data.productBrand,
            variant: data.productVariant,
            variant_id: data.productVariantId,
            category: data.productCategory,
            list: options.list,
            position: data.productPosition,
            price: data.productPrice,
          }
        })
      trackProductImpression(productData)

      // Next setup product click tracking.
      products.each(function(idx) {
        var $product = $(this),
        data = $product.data(),
        productData,
        variantData,
        destination

        // Find all trackable links in the product and add the event to them.
        $product.find('[data-track-product-click]')
          .on('click', function(e) {
            e.preventDefault()
            variantData = $(this).data()
            destination = this.href
            productData = {
              id: variantData.variantSku || data.productSku,
              product_id: data.productId,
              name: data.productName,
              brand: data.productBrand,
              variant: variantData.variantType || data.productVariant,
              variant_id: variantData.variantId,
              category: data.productCategory,
              position: data.productPosition,
              price: variantData.variantPrice || data.productPrice,
            }
            trackProductClick(productData, options.list, function() { Turbolinks.visit(destination) })
          })
      })
    }

    /**
    * Finds all child promos matching the given selector and uses each promo's
    * data attributes to compile a list of promos that have been viewed by the
    * user. Once the promo impressions are recorded, a click event is attached
    * to all links in the promo that have a `data-track-promo-click` attribute.
    * When one of these links is clicked, a promo click event is recorded.
    *
    * @function compilePromotionImpressions
    */
    function compilePromotionImpressions() {
      var promotions = $elem.find(options.selector),
      promoData

      // First track all promo impressions.
      promoData = promotions
        .toArray()
        .map(function(promo) {
          var data = $(promo).data()
          return {
            id: data.promoId,
            name: data.promoName,
            creative: data.promoCreative,
            position: data.promoPosition,
          }
        })
      trackPromotionImpression(promoData)

      // Next setup promo click tracking.
      promotions.each(function(idx) {
        var $promo = $(this),
        data = $promo.data(),
        promoData,
        destination

        // Find all trackable links in the promo and adds the event to them.
        $promo.find('[data-track-promo-click]')
          .on('click', function(e) {
            e.preventDefault()
            destination = this.href
            promoData = {
              id: data.promoId,
              name: data.promoName,
              creative: data.promoCreative,
              position: data.promoPosition,
            }
            trackPromotionClick(promoData, function() { Turbolinks.visit(destination) })
          })
      })
    }
  }

  /**
  * Represents information about a product that has been viewed. It is referred
  * to as an impressionFieldObject and contains the following values:
  *
  * @constructor
  * @param {string} id - The product ID or SKU (e.g. P67890). *Either this field or name must be set.
  * @param {string} name - The name of the product (e.g. Android T-Shirt). *Either this field or id must be set.
  * @param {string} list - The list or collection to which the product belongs (e.g. Search Results)
  * @param {string} brand - The brand associated with the product (e.g. Google).
  * @param {string} category - The category to which the product belongs (e.g. Apparel). Use / as a delimiter to specify up to 5-levels of hierarchy (e.g. Apparel/Men/T-Shirts).
  * @param {string} variant - The variant of the product (e.g. Black).
  * @param {integer} position - The product's position in a list or collection (e.g. 2).
  * @param {float} price - The price of a product (e.g. 29.20).
  *
  */
  function ImpressionFieldObject(data) {
    if (!data.id && !data.name) {
      throw new Error('ImpressionFieldObject: id or name must be present.')
    }

    var position = parseInt(data.position),
    price = parseFloat(data.price).toFixed(2)

    data = {
      'id': data.id ? String(data.id) : null,
      'name': data.name,
      'list': data.list,
      'brand': data.brand,
      'category': data.category,
      'variant': data.variant,
      'position': isNaN(position) ? null : position,
      'price': isNaN(price) ? null : price,
    }

    return removeEmpty(data)
  }

  /**
  * Product data represents individual products that were viewed, added to the shopping cart, etc.
  * It is referred to as a productFieldObject and contains the following values:
  *
  * @constructor
  * @param {string} id - The product ID or SKU (e.g. P67890). *Either this field or name must be set.
  * @param {string} name - The name of the product (e.g. Android T-Shirt). *Either this field or id must be set.
  * @param {string} brand - The brand associated with the product (e.g. Google).
  * @param {string} category - The category to which the product belongs (e.g. Apparel). Use / as a delimiter to specify up to 5-levels of hierarchy (e.g. Apparel/Men/T-Shirts).
  * @param {string} variant - The variant of the product (e.g. Black).
  * @param {float} price - The price of a product (e.g. 29.20).
  * @param {integer} quantity - The quantity of a product (e.g. 2).
  * @param {string} coupon - The coupon code associated with a product (e.g. SUMMER_SALE13).
  * @param {integer} position - The product's position in a list or collection (e.g. 2).
  */
  function ProductFieldObject(data) {
    if (!data.id && !data.name) {
      throw new Error('ProductFieldObject: id or name must be present.')
    }

    var price = parseFloat(data.price).toFixed(2),
    quantity = parseInt(data.quantity),
    position = parseInt(data.position)

    data = {
      'id': data.id ? String(data.id) : null,
      'product_id': data.product_id,
      'name': data.name,
      'brand': data.brand,
      'category': data.category,
      'variant': data.variant,
      'variant_id': data.variant_id,
      'price': isNaN(price) ? null : price,
      'quantity': isNaN(quantity) ? null : quantity,
      'coupon': data.coupon,
      'position': isNaN(position) ? null : position,
    }

    return removeEmpty(data)
  }


  /**
  * Represents information about a promotion that has been viewed. It is referred
  * to a promoFieldObject and contains the following values:
  *
  * @constructor
  * @param {string} id - The promotion ID (e.g. PROMO_1234). *Either this field or name must be set.
  * @param {string} name - The name of the promotion (e.g. Summer Sale). *Either this field or id must be set.
  * @param {string} creative - The creative associated with the promotion (e.g. summer_banner2).
  * @param {string} position - The position of the creative (e.g. banner_slot_1).
  */
  function PromoFieldObject(data) {
    if (!data.id && !data.name) {
      throw new Error('PromoFieldObject: id or name must be present.')
    }
    data = {
      'id': data.id ? String(data.id) : null,
      'name': data.name,
      'creative': data.creative,
      'position': data.position,
    }

    return removeEmpty(data)
  }

  /**
  * Represents information about an ecommerce related action that has taken place.
  * It is referred to as an actionFieldObject.
  *
  * Product and Promotion Actions
  * Actions specify how to interpret product and promotion data that you send to Google Analytics.
  *
  * click - A click on a product or product link for one or more products.
  * detail - A view of product details.
  * add - Adding one or more products to a shopping cart.
  * remove - Remove one or more products from a shopping cart.
  * checkout - Initiating the checkout process for one or more products.
  * checkout_option - Sending the option value for a given checkout step.
  * purchase - The sale of one or more products.
  * refund - The refund of one or more products.
  * promo_click - A click on an internal promotion.
  *
  *
  * @constructor
  * @param {string} action - see above for different types of actions.
  * @param {string} id - The transaction ID (e.g. T1234). *Required if the action type is purchase or refund.
  * @param {string} affiliation - The store or affiliation from which this transaction occurred (e.g. Google Store).
  * @param {float} revenue - Specifies the total revenue or grand total associated with the transaction (e.g. 11.99). This value may include shipping, tax costs, or other adjustments to total revenue that you want to include as part of your revenue calculations. Note: if revenue is not set, its value will be automatically calculated using the product quantity and price fields of all products in the same hit.
  * @param {float} tax - The total tax associated with the transaction.
  * @param {float} shipping - The shipping cost associated with the transaction.
  * @param {string} coupon - The transaction coupon redeemed with the transaction.
  * @param {string} list - The list that the associated products belong to. Optional.
  * @param {integer} step - A number representing a step in the checkout process. Optional on checkout actions.
  * @param {string} option - Additional field for checkout and checkout_option actions that can describe option information on the checkout page, like selected payment method.
  */
  function ActionFieldObject(data) {
    if ((data.action === 'purchase' || data.action === 'refund') && !data.id) {
      throw new Error('ActionFieldObject: id must be present for "purchase" and "refund" actions.')
    }
    var revenue = parseFloat(data.revenue).toFixed(2),
    tax = parseFloat(data.tax).toFixed(2),
    shipping = parseFloat(data.shipping).toFixed(2),
    step = parseInt(data.step)

    data = {
      'id': data.id ? String(data.id) : null,
      'affiliation': data.affiliation,
      'revenue': isNaN(revenue) ?  null : revenue,
      'tax': isNaN(tax) ? null : tax,
      'shipping': isNaN(shipping) ? null : shipping,
      'coupon': data.coupon,
      'list': data.list,
      'step': isNaN(step) ? null : step,
      'option': data.option,
    }

    return removeEmpty(data)
  }


  function removeEmpty(obj) {
    Object.keys(obj)
      .forEach(function(key) {
        !obj[key] && delete obj[key]
      })
    return obj
  }

}(jQuery))
